// PRD.282 — Clinic Type-Driven Patient Workspace Defaults
//
// One pure, deterministic preset builder shared by Clinic Settings and Patient
// Workspace Settings. It maps a clinic classification (primary type + optional
// additional specialties) to a suggested set of Patient Sidebar and Visit Note
// tab selections, keyed by the EXISTING Patient Workspace configuration keys.
//
// Constraints (PRD §7): no React state, storage, or DB access; unknown codes
// are ignored and logged (never crash); additional specialties only ADD tabs.
//
// Self-contained (no workspace-package imports) so it runs under node:test. The
// tab→UI-field maps below use the real `PatientWorkspaceSettingsUI` field names;
// `applyPresetToWorkspaceSettings` is generic so callers keep full type-safety
// at the call site (the pages pass their typed FormState).

import type { ClinicSpecialty, PrimaryClinicType } from './clinic-classification.js';

// ─── Tab keys (the real Patient Workspace identifiers) ───

export type PatientSidebarTabKey =
  | 'overview'
  | 'intake_form'
  | 'vitals'
  | 'allergies'
  | 'visits'
  | 'treatment_record'
  | 'contact'
  | 'pharmacy'
  | 'packages'
  | 'files_images'
  // PRD.305 — Patient Workspace Labs.
  | 'labs';

export type VisitNoteTabKey =
  | 'summary'
  | 'vitals'
  | 'charting'
  | 'notes'
  | 'prescriptions'
  | 'treatment_plan'
  | 'forms'
  | 'files'
  | 'invoices'
  | 'payments'
  // PRD.305 — Consultation / Visit Note Labs.
  | 'labs';

export interface ClinicWorkspacePreset {
  patientSidebarTabs: Record<string, boolean>;
  visitNoteTabs: Record<string, boolean>;
}

export interface ClinicWorkspacePresetInput {
  primaryClinicType: PrimaryClinicType;
  additionalSpecialties: ClinicSpecialty[];
}

// Map each tab key to its Patient Workspace UI field, so a preset can be merged
// into the existing settings model without inventing parallel tab settings.
// Values are the exact `PatientWorkspaceSettingsUI` field names.
const SIDEBAR_KEY_TO_UI_FIELD: Record<PatientSidebarTabKey, string> = {
  overview: 'sidebarShowOverview',
  intake_form: 'sidebarShowIntakeForm',
  vitals: 'sidebarShowVitals',
  allergies: 'sidebarShowAllergies',
  visits: 'sidebarShowVisits',
  treatment_record: 'sidebarShowTreatmentRecord',
  contact: 'sidebarShowContact',
  pharmacy: 'sidebarShowPharmacy',
  packages: 'sidebarShowPackages',
  files_images: 'sidebarShowFilesImages',
  labs: 'sidebarShowLabs',
};

const VISIT_KEY_TO_UI_FIELD: Record<VisitNoteTabKey, string> = {
  summary: 'consultShowSummary',
  vitals: 'consultShowVitals',
  charting: 'consultShowCharting',
  notes: 'consultShowNotes',
  prescriptions: 'consultShowPrescriptions',
  treatment_plan: 'consultShowTreatmentPlan',
  forms: 'consultShowForms',
  files: 'consultShowFiles',
  invoices: 'consultShowInvoices',
  payments: 'consultShowPayments',
  labs: 'consultShowLabs',
};

// ─── Base presets (PRD §3) ───

// General (PRD §3.1): Treatment Record + Charting OFF, everything else ON.
const GENERAL_SIDEBAR: Record<PatientSidebarTabKey, boolean> = {
  overview: true,
  intake_form: true,
  vitals: true,
  allergies: true,
  visits: true,
  treatment_record: false,
  contact: true,
  pharmacy: true,
  packages: true,
  // PRD.286 — master file repository; on for every clinic type.
  files_images: true,
  // PRD.305 — every clinic type orders laboratory work, so Labs is on in the
  // base preset. Laboratory Settings and RBAC decide how much of it is usable.
  labs: true,
};

const GENERAL_VISIT: Record<VisitNoteTabKey, boolean> = {
  summary: true,
  vitals: true,
  charting: false,
  notes: true,
  prescriptions: true,
  treatment_plan: true,
  forms: true,
  files: true,
  invoices: true,
  payments: true,
  // PRD.305 — Labs is a normal configurable Visit Note sub-module.
  labs: true,
};

// Dermatology / Aesthetic (PRD §3.2) and Dental (PRD §3.3) enable every tab —
// they are the General preset with Treatment Record + Charting turned on.
const ALL_ON_SIDEBAR: Record<PatientSidebarTabKey, boolean> = {
  ...GENERAL_SIDEBAR,
  treatment_record: true,
};

const ALL_ON_VISIT: Record<VisitNoteTabKey, boolean> = {
  ...GENERAL_VISIT,
  charting: true,
};

function primaryPreset(primary: PrimaryClinicType): ClinicWorkspacePreset {
  switch (primary) {
    case 'dermatology_aesthetic':
    case 'dental':
      return { patientSidebarTabs: { ...ALL_ON_SIDEBAR }, visitNoteTabs: { ...ALL_ON_VISIT } };
    case 'general':
    case 'other': // PRD §3.4 — 'other' uses the General preset as its base.
      return { patientSidebarTabs: { ...GENERAL_SIDEBAR }, visitNoteTabs: { ...GENERAL_VISIT } };
    default:
      // Unknown primary — never crash; fall back to General (PRD §7).
      console.warn('[clinic-workspace-preset] unknown primary clinic type; using General', primary);
      return { patientSidebarTabs: { ...GENERAL_SIDEBAR }, visitNoteTabs: { ...GENERAL_VISIT } };
  }
}

// ─── Additional specialty additions (PRD §4) — additive only ───

const SPECIALTY_ADDITIONS: Record<
  ClinicSpecialty,
  { sidebar: PatientSidebarTabKey[]; visit: VisitNoteTabKey[] }
> = {
  general_medicine: { sidebar: [], visit: [] },
  dermatology: { sidebar: ['treatment_record'], visit: ['charting'] },
  aesthetic_medicine: { sidebar: ['treatment_record'], visit: ['charting'] },
  dental: { sidebar: ['treatment_record'], visit: ['charting'] },
  pediatrics: { sidebar: [], visit: [] },
  ent: { sidebar: [], visit: [] },
  ob_gyn: { sidebar: [], visit: [] },
  other: { sidebar: [], visit: [] },
};

/**
 * Build the suggested Patient Workspace preset for a clinic classification.
 *
 * Deterministic and pure. Additional specialties only enable tabs (never
 * disable), and unknown specialty codes are ignored with a warning so a bad
 * value can't break the page.
 */
export function buildClinicWorkspacePreset(
  input: ClinicWorkspacePresetInput,
): ClinicWorkspacePreset {
  const base = primaryPreset(input.primaryClinicType);
  const patientSidebarTabs: Record<string, boolean> = { ...base.patientSidebarTabs };
  const visitNoteTabs: Record<string, boolean> = { ...base.visitNoteTabs };

  for (const specialty of input.additionalSpecialties ?? []) {
    const additions = SPECIALTY_ADDITIONS[specialty];
    if (!additions) {
      console.warn('[clinic-workspace-preset] ignoring unknown specialty', specialty);
      continue;
    }
    for (const key of additions.sidebar) patientSidebarTabs[key] = true; // never disable
    for (const key of additions.visit) visitNoteTabs[key] = true;
  }

  return { patientSidebarTabs, visitNoteTabs };
}

/**
 * Merge a preset's tab selections into an existing Patient Workspace settings
 * value, replacing ONLY the configurable Sidebar/Visit Note tab booleans and
 * preserving every non-tab setting (workspace mode, drawer width, queue
 * actions, etc.). Used by both pages so "Apply Clinic Defaults" stays a
 * single-source-of-truth transformation.
 */
export function applyPresetToWorkspaceSettings<T extends object>(
  current: T,
  preset: ClinicWorkspacePreset,
): T {
  const next = { ...current } as Record<string, unknown>;
  for (const [key, field] of Object.entries(SIDEBAR_KEY_TO_UI_FIELD)) {
    if (Object.prototype.hasOwnProperty.call(preset.patientSidebarTabs, key)) {
      next[field] = preset.patientSidebarTabs[key];
    }
  }
  for (const [key, field] of Object.entries(VISIT_KEY_TO_UI_FIELD)) {
    if (Object.prototype.hasOwnProperty.call(preset.visitNoteTabs, key)) {
      next[field] = preset.visitNoteTabs[key];
    }
  }
  return next as T;
}
